import _ from 'underscore';

import { getYahooModel } from '@bingads-webui-react/yahoo-iframe';

import { buildToobarConfig, ToolbarConfigs } from '@bingads-webui-campaign-react/accounts-summary-common';
import { getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';

export const getToolbarConfig = (context) => {
  const {
    permissions,
    onSelectCrossPage,
    i18n,
    scope,
    serverContext: {
      CurrentUser,
    },
    siteMap,
    getBulkUnlinkAccounts,
    getBulkTransferAccounts,
  } = context;

  const dynamicToolbarItemsKeys = {
    unlinkAccount: 'unlink-account',
    transferAccount: 'transfer-account',
    bamDropdownEdit: 'bam-dropdown-edit',
  };

  const { canUserAccessMicrosoftData, isManagedByYahoo } = getYahooModel({
    isCustomerManagedByYahoo: CurrentUser.ManagedBy === 'Yahoo',
    isInternalUser: CurrentUser.IsInternal,
    permissions: permissions.dynamic,
  });

  const getBulkEditDropdown = ({
    gridView,
  }) => {
    const selectedAccounts = gridView ? gridView.selectedItems() : null;
    const hasManagerAccount = selectedAccounts && _.some(selectedAccounts, account => _.isObject(account) && account.AccountType === 'ManagerAccount');

    return permissions.IsCampaignBAMEnabled && [dynamicToolbarItemsKeys.bamDropdownEdit, {
      id: dynamicToolbarItemsKeys.bamDropdownEdit,
      type: 'dropdown',
      iconLeftGlyphName: 'ba-Edit',
      button: {
        text: i18n.getString(_TL_('Edit')),
      },
      menu: {
        items: [{
          text: i18n.getString(_TL_('Build spreadsheet')),
          id: 'dropdown-bam-spreadsheet',
          onClick: () => { scope.save(getRoutePathById(siteMap, 'build-spreadsheet')); },
        }, {
          text: i18n.getString(_TL_('Upload')),
          id: 'dropdown-bam-upload',
          onClick: () => { scope.save('../bulkuploads/uploads'); },
        }],
      },
      disabled: hasManagerAccount,
    }];
  };

  const toolbarSelectedConfig = buildToobarConfig([
    // Indicates how many grid rows are selected
    ['selection-widget', {
      type: 'selection-widget',
      onSelectCrossPage,
    }],
    // The faint vertical separate next to the chart toggle
    ['divider', {
      type: 'divider',
    }],
    [dynamicToolbarItemsKeys.unlinkAccount, {}],
    permissions && permissions.IsAccountLabelsEnabled && ['dropdown-labels', {
      type: 'dropdown',
      id: 'dropdown-labels',
    }],
    [dynamicToolbarItemsKeys.transferAccount, {}],
    [dynamicToolbarItemsKeys.bamDropdownEdit, {}],
    // Separates the left-float and right-float nav items
    ['expander', {
      type: 'expander',
    }],
    // The "X" to return to the unselected toolbar
    ['button-clear-selection', {
      type: 'button-clear-selection',
    }],
    ['button-chart-toggle', {
      id: 'button-chart-toggle',
      type: 'button',
    }],
  ]);

  const toolbarDynamicSelectedConfigCallback = ({ gridView } = {}) => buildToobarConfig([
    ToolbarConfigs.getUnlinkAccountNavItem({
      key: dynamicToolbarItemsKeys.unlinkAccount, context, gridView, getBulkUnlinkAccounts,
    }),
    ToolbarConfigs.getTransferAccountNavItem({
      key: dynamicToolbarItemsKeys.transferAccount, context, gridView, getBulkTransferAccounts,
    }),
    getBulkEditDropdown({ gridView }),
  ]);

  const toolbarUnselectedConfig = buildToobarConfig([
    permissions.IsOBOAgencyCreateAccountEnabled
      ? ToolbarConfigs.getCreateAccountOBOButton({ context })
      : canUserAccessMicrosoftData && ToolbarConfigs.getCreateAccountButton({ context }),
    !isManagedByYahoo && ToolbarConfigs.getSendRequestNavItem({ context }),
    getBulkEditDropdown({}),
    ['predicate-list', {
      id: 'predicate-list',
      type: 'custom',
    }],
    ['search-box-filter', {
      id: 'search-box-filter',
      type: 'search-box',
    }],
    permissions.IsCustomerHierarchyEnabled &&
    ['accountsSelection_selector', {
      id: 'accountsSelection_selector',
    }],
    ['dropdown-column-chooser', {
      id: 'dropdown-column-chooser',
      type: 'dropdown',
    }],
    ['dropdown-segment', {
      id: 'dropdown-segment',
      type: 'dropdown',
      button: {
        // This text is overwritten with a localized string by the segment plugin
        text: 'segment-placeholder',
      },
    }],
    ['button-download', {
      id: 'button-download',
      type: 'nav-item',
      iconLeftGlyphName: 'ba-Download',
    }],
    // The faint vertical separator next to the chart toggle
    ['divider', {
      type: 'divider',
    }],
    ['button-chart-toggle', {
      id: 'button-chart-toggle',
      type: 'button',
    }],
  ]);

  return {
    toolbarSelectedConfig,
    toolbarDynamicSelectedConfigCallback,
    toolbarUnselectedConfig,
  };
};
